import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Radio as PodcastIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import {
  fetchPodcasts,
  deletePodcast,
  Podcast,
  PaginatedResponse,
} from "../../services/api";

const PodcastList: React.FC = () => {
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<PaginatedResponse<Podcast>>({
    queryKey: ["podcasts", page + 1, rowsPerPage],
    queryFn: () => fetchPodcasts(page + 1, rowsPerPage),
    placeholderData: (previousData) => previousData,
    staleTime: 5000,
  });

  const deleteMutation = useMutation({
    mutationFn: deletePodcast,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["podcasts"] });
    },
  });

  const handleDeleteClick = (id: number) => setDeleteId(id);
  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };
  const handleDeleteCancel = () => setDeleteId(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to format time for display in AM/PM format
  const formatTime = (timeSpan?: string): string => {
    if (!timeSpan) return "";

    // If timeSpan is in TimeSpan format like "00:30:00"
    if (timeSpan.includes(":")) {
      const parts = timeSpan.split(":");
      if (parts.length >= 2) {
        const hour = parseInt(parts[0], 10);
        const minute = parts[1].padStart(2, "0");

        // Convert to 12-hour format with AM/PM
        if (hour === 0) {
          return `12:${minute} AM`;
        } else if (hour < 12) {
          return `${hour}:${minute} AM`;
        } else if (hour === 12) {
          return `12:${minute} PM`;
        } else {
          return `${hour - 12}:${minute} PM`;
        }
      }
    }
    return timeSpan;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">
          Error loading podcasts. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Podcasts</Typography>
        <Button
          component={Link}
          to="/podcasts/new"
          variant="contained"
          color="primary"
          startIcon={<PodcastIcon />}
        >
          Add New Podcast
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Short Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Downloadable</TableCell>
              <TableCell width="200">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items.map((podcast: Podcast) => (
              <TableRow key={podcast.id}>
                <TableCell>{podcast.title}</TableCell>
                <TableCell>{podcast.shortDescription}</TableCell>
                <TableCell>{podcast.podcastTypeName}</TableCell>
                <TableCell>
                  {podcast.timeSlots && podcast.timeSlots.length > 0 ? (
                    <Stack spacing={0.5}>
                      {podcast.timeSlots.slice(0, 3).map((slot, index) => (
                        <Chip
                          key={index}
                          icon={<TimeIcon fontSize="small" />}
                          label={`${formatTime(slot.startTime)} - ${formatTime(
                            slot.endTime
                          )}`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ))}
                      {podcast.timeSlots.length > 3 && (
                        <Tooltip
                          title={`${
                            podcast.timeSlots.length - 3
                          } more time slot(s)`}
                        >
                          <Chip
                            icon={<TimeIcon fontSize="small" />}
                            label={`+${podcast.timeSlots.length - 3} more`}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Not scheduled
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={podcast.active ? "Active" : "Inactive"}
                    color={podcast.active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={podcast.isDownloadable ? "Yes" : "No"}
                    color={podcast.isDownloadable ? "primary" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Edit Podcast">
                      <IconButton
                        component={Link}
                        to={`/podcasts/edit/${podcast.id}`}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Manage Episodes">
                      <IconButton
                        component={Link}
                        to={`/podcasts/${podcast.id}/episodes`}
                        size="small"
                        color="secondary"
                      >
                        <PodcastIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Podcast">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(podcast.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {!data?.items.length && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" sx={{ py: 2 }}>
                    No podcasts found. Create your first podcast!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data && data.items.length > 0 && (
        <TablePagination
          component="div"
          count={data.totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />
      )}

      <Dialog open={deleteId !== null} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this podcast? This will also delete
            all episodes associated with this podcast. This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PodcastList;
